// utils/activityExcel.js - 活動 Excel 處理工具
import * as XLSX from 'xlsx';
import axios from './axiosConfig';

// 下載活動 Excel 檔案
export const downloadActivitiesExcel = async (token) => {
  try {
    console.log('開始下載活動 Excel...');
    console.log('使用 token:', token ? `${token.substring(0, 20)}...` : 'null');
    
    const response = await axios.get('/activities/export', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob', // 重要：設定為 blob 類型
    });

    console.log('收到回應:', response.status, response.headers);
    console.log('Content-Type:', response.headers['content-type']);

    // 檢查回應是否為 Excel 檔案
    const contentType = response.headers['content-type'];
    if (!contentType || !contentType.includes('spreadsheetml')) {
      // 如果是 JSON 錯誤回應，解析並顯示錯誤訊息
      if (contentType && contentType.includes('application/json')) {
        try {
          const reader = new FileReader();
          const errorData = await new Promise((resolve, reject) => {
            reader.onload = () => {
              try {
                const result = JSON.parse(reader.result);
                console.log('解析到的錯誤資料:', result);
                resolve(result);
              } catch (e) {
                console.error('JSON 解析失敗:', e);
                reject(e);
              }
            };
            reader.onerror = reject;
            reader.readAsText(response.data);
          });
          throw new Error(errorData.message || errorData.error || '伺服器返回錯誤');
        } catch (parseError) {
          console.error('解析錯誤回應失敗:', parseError);
          // 嘗試直接讀取 response.data 作為字串
          try {
            const textData = await response.data.text();
            console.log('原始回應內容:', textData);
            throw new Error(`伺服器錯誤: ${textData}`);
          } catch (textError) {
            throw new Error('伺服器返回錯誤，但無法解析錯誤訊息');
          }
        }
      }
      throw new Error(`回應不是 Excel 檔案格式，收到的格式：${contentType}`);
    }

    // 建立正確的 Blob
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    // 建立下載連結
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // 從 response headers 取得檔名
    let fileName = `活動資料_${new Date().toISOString().slice(0, 10)}.xlsx`;
    const contentDisposition = response.headers['content-disposition'];
    
    if (contentDisposition) {
      // 處理 UTF-8 編碼的檔名
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (utf8Match) {
        fileName = decodeURIComponent(utf8Match[1]);
      } else {
        const regularMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (regularMatch && regularMatch[1]) {
          fileName = regularMatch[1].replace(/['"]/g, '');
        }
      }
    }
    
    console.log('下載檔名:', fileName);
    
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // 清理
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    return { success: true, message: '活動資料下載成功！' };
  } catch (error) {
    console.error('下載活動 Excel 失敗:', error);
    
    let errorMessage = '下載失敗，請稍後再試';
    if (error.response?.status === 403) {
      errorMessage = '權限不足：只有秘書才能下載活動資料';
    } else if (error.response?.status === 401) {
      errorMessage = '請重新登入後再試';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = `下載失敗: ${error.message}`;
    }
    
    return { success: false, message: errorMessage };
  }
};

// 處理 Excel 檔案上傳
export const handleExcelFileUpload = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('請選擇要上傳的檔案'));
      return;
    }

    // 檢查檔案類型
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];
    
    if (!validTypes.includes(file.type)) {
      reject(new Error('請選擇 Excel 檔案 (.xlsx 或 .xls)'));
      return;
    }

    // 檢查檔案大小 (限制 10MB)
    if (file.size > 10 * 1024 * 1024) {
      reject(new Error('檔案大小不能超過 10MB'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // 取得第一個工作表
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // 轉換為 JSON 格式
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (jsonData.length === 0) {
          reject(new Error('Excel 檔案沒有資料'));
          return;
        }

        // 驗證必要欄位
        const requiredColumns = ['活動名稱', '開始日期', '結束日期'];
        const firstRow = jsonData[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));

        if (missingColumns.length > 0) {
          reject(new Error(`缺少必要欄位：${missingColumns.join(', ')}`));
          return;
        }

        // 驗證日期格式
        const dateColumns = ['開始日期', '結束日期', '活動日期', '報名結束日期'];
        const dateErrors = [];

        // 日期格式驗證函數
        const isValidDateFormat = (value) => {
          if (!value) return true; // 空值允許
          const str = String(value).trim();
          // 允許格式：2025-01-23、2025/01/23、2025-1-23 09:00、2025-01-23 09:00:00、Excel數值
          if (typeof value === 'number') return true; // Excel 數值日期
          return /^\d{4}[-/]\d{1,2}[-/]\d{1,2}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/.test(str);
        };

        jsonData.forEach((row, index) => {
          const rowNum = index + 2; // Excel 行號
          dateColumns.forEach(col => {
            if (row[col] && !isValidDateFormat(row[col])) {
              dateErrors.push(`第${rowNum}行「${col}」格式錯誤：${row[col]}`);
            }
          });
        });

        if (dateErrors.length > 0) {
          const errorMsg = dateErrors.length > 5
            ? `${dateErrors.slice(0, 5).join('\n')}\n... 還有 ${dateErrors.length - 5} 個錯誤`
            : dateErrors.join('\n');
          reject(new Error(`日期格式驗證失敗：\n${errorMsg}\n\n請使用標準格式如：2025-01-23 或 2025-01-23 09:00:00`));
          return;
        }

        resolve({
          success: true,
          data: jsonData,
          message: `成功讀取 ${jsonData.length} 筆活動資料`
        });
        
      } catch (error) {
        console.error('解析 Excel 檔案失敗:', error);
        reject(new Error('Excel 檔案格式錯誤，請檢查檔案內容'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('讀取檔案失敗'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

// 上傳活動資料到後端
export const uploadActivitiesData = async (excelData, token) => {
  try {
    const response = await axios.post('/activities/import', {
      excelData
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return {
      success: true,
      data: response.data,
      message: response.data.message || '匯入成功！'
    };
    
  } catch (error) {
    console.error('上傳活動資料失敗:', error);
    
    let errorMessage = '上傳失敗，請稍後再試';
    if (error.response?.status === 403) {
      errorMessage = '權限不足：只有秘書才能匯入活動資料';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    return {
      success: false,
      message: errorMessage,
      error: error.response?.data || error.message
    };
  }
};

// 格式化匯入結果訊息
export const formatImportResults = (results) => {
  if (!results || !results.results) {
    return '匯入完成';
  }

  const { success, failed, errors } = results.results;
  let message = `匯入完成：成功 ${success} 筆`;
  
  if (failed > 0) {
    message += `，失敗 ${failed} 筆`;
    
    if (errors && errors.length > 0) {
      message += '\n\n失敗項目：\n';
      errors.slice(0, 5).forEach(error => {
        message += `• 第${error.row}行 (${error.activity}): ${error.error}\n`;
      });
      
      if (errors.length > 5) {
        message += `• 還有 ${errors.length - 5} 個錯誤...`;
      }
    }
  }
  
  return message;
};